import * as THREE from 'three'
import { worldWidth, worldDepth } from '../utils/grid'

/** Ambient rising bubbles (Points) + a small pooled burst system for pops/captures. */
export class Bubbles {
  readonly group = new THREE.Group()
  private ambient: THREE.Points
  private velocities: Float32Array
  private count: number
  private bounds = { w: 0, d: 0, top: 22 }

  // Burst pool
  private burst: THREE.Points
  private burstPos: Float32Array
  private burstVel: Float32Array
  private burstLife: Float32Array
  private burstMax = 120
  private burstHead = 0

  constructor(count = 220) {
    this.count = count
    this.bounds.w = worldWidth() + 8
    this.bounds.d = worldDepth() + 8

    const tex = this.bubbleTexture()

    // Ambient field
    const positions = new Float32Array(count * 3)
    this.velocities = new Float32Array(count)
    const sizes = new Float32Array(count)
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * this.bounds.w
      positions[i * 3 + 1] = Math.random() * this.bounds.top - 2
      positions[i * 3 + 2] = (Math.random() - 0.5) * this.bounds.d
      this.velocities[i] = 0.6 + Math.random() * 1.4
      sizes[i] = 0.08 + Math.random() * 0.22
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
    const mat = new THREE.PointsMaterial({
      map: tex,
      size: 0.3,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
      color: 0xcdf2ff,
    })
    this.ambient = new THREE.Points(geo, mat)
    this.group.add(this.ambient)

    // Burst pool
    this.burstPos = new Float32Array(this.burstMax * 3)
    this.burstVel = new Float32Array(this.burstMax * 3)
    this.burstLife = new Float32Array(this.burstMax)
    const bgeo = new THREE.BufferGeometry()
    bgeo.setAttribute('position', new THREE.BufferAttribute(this.burstPos, 3))
    bgeo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.burstMax * 3), 3))
    const bmat = new THREE.PointsMaterial({
      map: tex,
      size: 0.4,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
      color: 0xffffff,
      vertexColors: true,
    })
    this.burst = new THREE.Points(bgeo, bmat)
    this.group.add(this.burst)
  }

  private bubbleTexture(): THREE.CanvasTexture {
    const cv = document.createElement('canvas')
    cv.width = cv.height = 64
    const ctx = cv.getContext('2d')!
    const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30)
    g.addColorStop(0, 'rgba(255,255,255,0.95)')
    g.addColorStop(0.3, 'rgba(200,240,255,0.5)')
    g.addColorStop(1, 'rgba(200,240,255,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(32, 32, 30, 0, Math.PI * 2)
    ctx.fill()
    // rim highlight
    ctx.strokeStyle = 'rgba(255,255,255,0.6)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(24, 22, 6, 0, Math.PI * 2)
    ctx.stroke()
    return new THREE.CanvasTexture(cv)
  }

  /** Emit a burst of bubbles at a world position (capture, pellet pop, etc). */
  emitBurst(x: number, y: number, z: number, n = 14, color = 0xffffff): void {
    const tint = new THREE.Color(color)
    const colors = this.burst.geometry.getAttribute('color') as THREE.BufferAttribute
    for (let k = 0; k < n; k++) {
      const i = this.burstHead
      this.burstHead = (this.burstHead + 1) % this.burstMax
      this.burstPos[i * 3] = x
      this.burstPos[i * 3 + 1] = y
      this.burstPos[i * 3 + 2] = z
      const a = Math.random() * Math.PI * 2
      const sp = 1.5 + Math.random() * 3
      this.burstVel[i * 3] = Math.cos(a) * sp
      this.burstVel[i * 3 + 1] = 1.5 + Math.random() * 3
      this.burstVel[i * 3 + 2] = Math.sin(a) * sp
      this.burstLife[i] = 0.7 + Math.random() * 0.4
      colors.setXYZ(i, tint.r, tint.g, tint.b)
    }
    colors.needsUpdate = true
  }

  update(dt: number): void {
    const pos = this.ambient.geometry.attributes.position as THREE.BufferAttribute
    const arr = pos.array as Float32Array
    for (let i = 0; i < this.count; i++) {
      arr[i * 3 + 1] += this.velocities[i] * dt
      arr[i * 3] += Math.sin((arr[i * 3 + 1] + i) * 1.5) * dt * 0.3
      if (arr[i * 3 + 1] > this.bounds.top) {
        arr[i * 3 + 1] = -2
        arr[i * 3] = (Math.random() - 0.5) * this.bounds.w
        arr[i * 3 + 2] = (Math.random() - 0.5) * this.bounds.d
      }
    }
    pos.needsUpdate = true

    const bp = this.burst.geometry.attributes.position as THREE.BufferAttribute
    const ba = bp.array as Float32Array
    for (let i = 0; i < this.burstMax; i++) {
      if (this.burstLife[i] <= 0) {
        ba[i * 3 + 1] = -999
        continue
      }
      this.burstLife[i] -= dt
      this.burstVel[i * 3 + 1] += dt * 1.5
      ba[i * 3] = this.burstPos[i * 3] += this.burstVel[i * 3] * dt
      ba[i * 3 + 1] = this.burstPos[i * 3 + 1] += this.burstVel[i * 3 + 1] * dt
      ba[i * 3 + 2] = this.burstPos[i * 3 + 2] += this.burstVel[i * 3 + 2] * dt
    }
    bp.needsUpdate = true
  }
}
